/**
 * UI Atoms - Global state for UI behavior and settings
 *
 * Uses Jotai for atom-based state management with localStorage persistence
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Debug mode toggle
 * Controls whether the chat view shows:
 * - true: DEBUG view (raw tool calls, full responses)
 * - false: USER view (explanations only, thinking spinner, expandable details)
 *
 * Persisted to localStorage as 'debugMode'
 */
export const debugModeAtom = atomWithStorage<boolean>('debugMode', false);

/**
 * "(no query)" diagnostic mode
 * When on, layouts surface faded tabs for relationships declared on
 * the model but neither loaded nor reachable via a registered query
 * — useful for spotting "I'd expect to navigate here but can't".
 * Off by default. Toggle via `?no_query=1|0` URL param (handled by
 * `useNoQueryMode`); persists across pages until explicitly changed.
 */
export const noQueryModeAtom = atomWithStorage<boolean>('noQueryMode', false);

/**
 * Admin mode toggle (for admin users only)
 * When enabled, admin users can bypass workspace security filters
 * to view data across all workspaces.
 *
 * NOT persisted - requires explicit re-activation each session for security.
 * Synced with backend via POST /auth/admin-mode.
 */
export const adminModeAtom = atom<boolean>(false);

/**
 * Admin write mode toggle (for admin users only)
 * When enabled (AND admin mode is on), admin users can write to
 * other workspaces. Without this, admin mode is read-only for other workspaces.
 *
 * NOT persisted - requires explicit re-activation each session for security.
 * Synced with backend via POST /auth/admin-write-mode.
 */
export const adminWriteModeAtom = atom<boolean>(false);

/**
 * Expanded tool calls tracking
 * Tracks which tool call IDs are expanded in user-focused view
 * Used to show/hide raw data when user clicks "show details"
 */
export const expandedToolCallsAtom = atom<Set<string>>(new Set<string>());

/**
 * Toggle a tool call's expanded state
 */
export const toggleToolCallExpandAtom = atom(
  null,
  (get, set, toolCallId: string) => {
    const expanded = get(expandedToolCallsAtom);
    const newExpanded = new Set(expanded);
    if (newExpanded.has(toolCallId)) {
      newExpanded.delete(toolCallId);
    } else {
      newExpanded.add(toolCallId);
    }
    set(expandedToolCallsAtom, newExpanded);
  }
);

/**
 * Check if a specific tool call is expanded
 */
export const isToolCallExpandedAtom = atom(
  null,
  (get, _set, toolCallId: string) => {
    return get(expandedToolCallsAtom).has(toolCallId);
  }
);
